import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Battery, Gift, X, Sparkles } from 'lucide-react';
import { showCombinedRewardedAds } from '../../hooks/useCombinedAds';
import { autoFaucetApi } from '../../api';
import toast from 'react-hot-toast';

const DAILY_ENERGY_CLAIMED_KEY = 'gemifaucet_daily_energy_claimed';
const DAILY_ENERGY_SHOWN_KEY = 'gemifaucet_daily_energy_shown';
const FREE_ENERGY_AMOUNT = 50;

interface DailyEnergyPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newEnergy: number) => void;
}

/**
 * Check if daily energy popup has already been shown today
 * This ensures the popup only shows once per 24 hours
 */
export function hasClaimedDailyEnergy(): boolean {
  const lastShown = localStorage.getItem(DAILY_ENERGY_SHOWN_KEY);
  if (!lastShown) return false;
  
  const today = new Date().toDateString();
  return lastShown === today;
}

/**
 * Mark daily energy popup as shown for today
 * Called when the popup is opened or dismissed
 */
export function markDailyEnergyClaimed(): void {
  localStorage.setItem(DAILY_ENERGY_SHOWN_KEY, new Date().toDateString());
}

/**
 * Check if user has actually claimed energy today (used by backend)
 */
export function hasActuallyClaimedToday(): boolean {
  const lastClaimed = localStorage.getItem(DAILY_ENERGY_CLAIMED_KEY);
  if (!lastClaimed) return false;
  
  const today = new Date().toDateString();
  return lastClaimed === today;
}

/**
 * Mark energy as actually claimed (after successful API call)
 */
function markEnergyActuallyClaimed(): void {
  localStorage.setItem(DAILY_ENERGY_CLAIMED_KEY, new Date().toDateString());
}

export function DailyEnergyPopup({ isOpen, onClose, onSuccess }: DailyEnergyPopupProps) {
  const [isClaiming, setIsClaiming] = useState(false);

  // Mark as shown when popup opens
  useEffect(() => {
    if (isOpen) {
      markDailyEnergyClaimed();
    }
  }, [isOpen]);

  const handleClaim = async () => {
    setIsClaiming(true);
    
    try {
      // Show combined rewarded ads first (AdsGram + Onclicka)
      toast.loading('Showing ads...', { id: 'daily-energy-ads' });
      const adResult = await showCombinedRewardedAds('daily_energy');
      toast.dismiss('daily-energy-ads');
      
      if (!adResult.success) {
        toast.error('Please watch the ads to claim your free energy');
        setIsClaiming(false);
        return;
      }
      
      // Call backend to add free energy
      const result = await autoFaucetApi.claimDailyFreeEnergy();
      
      if (result.success) {
        markEnergyActuallyClaimed(); // Mark as actually claimed
        toast.success(`+${FREE_ENERGY_AMOUNT} free energy claimed!`);
        onSuccess(result.newEnergy);
        onClose();
      } else {
        toast.error(result.error || 'Failed to claim free energy');
      }
    } catch (error: any) {
      console.error('Failed to claim daily energy:', error);
      toast.error(error.response?.data?.error || 'Failed to claim free energy');
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-sm bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>

            {/* Decorative background */}
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-green-500/10" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-yellow-500/20 rounded-full blur-3xl" />
            
            {/* Content */}
            <div className="relative p-6 pt-8 text-center">
              {/* Icon */}
              <div className="relative mx-auto w-20 h-20 mb-4">
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/30"
                >
                  <Battery className="w-10 h-10 text-white" />
                </motion.div>
                
                {/* Sparkles */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="absolute -top-2 -right-2"
                >
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                </motion.div>
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                  className="absolute -bottom-1 -left-2"
                >
                  <Sparkles className="w-5 h-5 text-green-400" />
                </motion.div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-white mb-2">
                Daily Free Energy! ⚡
              </h2>
              
              {/* Description */}
              <p className="text-white/60 mb-4">
                Claim your daily free energy to power up your auto faucet!
              </p>

              {/* Energy amount */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 mb-6">
                <Gift className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-bold text-lg">+{FREE_ENERGY_AMOUNT} Energy</span>
              </div>

              {/* Claim button */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleClaim}
                disabled={isClaiming}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-yellow-500/30 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isClaiming ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Gift className="w-5 h-5" />
                    Watch Ad & Claim
                  </>
                )}
              </motion.button>

              {/* Skip text */}
              <p className="text-white/40 text-xs mt-3">
                You can also claim this later from the Earn page
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
